import {DocumentItem} from '../document-list.models';

export const START_APPROVAL_ACTION = 'startApproval';

export const ARCHIVE_ACTION = 'archive';

export const RENAME_ACTION = 'rename';

export const REPLACE_ACTION = 'replace';

export const ApprovalState = {
  NOT_REQUIRED: -1,
  NOT_STARTED: -2,
  IN_PROGRESS: 0,
  COMPLETED: 1
} as const;

export interface OutcomeMessage {
  status: 'success' | 'failure' | 'warning';
  key: string;
}

export function approvalStateOf(row: DocumentItem): number {
  if (!row.isDocumentApprovalRequired) {
    return ApprovalState.NOT_REQUIRED;
  }
  if (!row.isDocumentApprovalWorkflowStarted) {
    return ApprovalState.NOT_STARTED;
  }
  return row.isDocumentApprovalProcessed ? ApprovalState.COMPLETED : ApprovalState.IN_PROGRESS;
}

export function circuitIdOf(row: DocumentItem): number | undefined {
  return row.awfAwardFinalizationId ?? row.finEngagementDocId ?? row.finBoardDecisionId;
}

export function isApprovalEligible(row: DocumentItem): boolean {
  return !!row
    && row.edmRepositoryDocumentId != null
    && circuitIdOf(row) != null
    && row.canReplace === true
    && row.isDocumentApprovalRequired === true;
}

export function isAwfCompleted(row: DocumentItem): boolean {
  return !!row && row.isAwfCompleted === true;
}

export function isApprovalStartable(row: DocumentItem): boolean {
  return !isAwfCompleted(row)
    && isApprovalEligible(row)
    && row.isDocumentApprovalProcessed === ApprovalState.NOT_STARTED;
}

export function isArchiveAction(action: string): boolean {
  return action === ARCHIVE_ACTION;
}

export function isArchiveBlockedByApproval(row: DocumentItem): boolean {
  return isAwfCompleted(row)
    || (!!row && circuitIdOf(row) != null && row.isDocumentApprovalFinal === true);
}

export function isReplaceBlockedByClosedAward(row: DocumentItem): boolean {
  return isAwfCompleted(row);
}

export function isUnknownRequestOutcome(error: unknown): boolean {
  const requestError = error as { name?: string; status?: number };
  return requestError?.name === 'TimeoutError' ||
    requestError?.status === 0 ||
    requestError?.status === 408 ||
    (typeof requestError?.status === 'number' && requestError.status >= 500);
}

const CONFLICT: OutcomeMessage = {
  status: 'failure',
  key: 'ecase.explorer.documentList.conflict'
};

const NOT_STARTED: OutcomeMessage = {
  status: 'warning',
  key: 'ecase.explorer.documentList.approval.notStarted'
};

const UNAVAILABLE: OutcomeMessage = {
  status: 'warning',
  key: 'ecase.explorer.documentList.approval.unavailable'
};

export const OUTCOME_UNKNOWN: OutcomeMessage = {
  status: 'warning',
  key: 'ecase.explorer.documentList.replace.outcomeUnknown'
};

const REPLACE_FAILED: OutcomeMessage = {
  status: 'failure',
  key: 'ecase.explorer.documentList.replace.error'
};

const ARCHIVED: OutcomeMessage = {
  status: 'success',
  key: 'ecase.explorer.documentList.archive.success'
};

const ARCHIVE_APPROVED: OutcomeMessage = {
  status: 'warning',
  key: 'ecase.explorer.documentList.archive.approvedCannotArchive'
};

const ARCHIVE_AWF_COMPLETED: OutcomeMessage = {
  status: 'warning',
  key: 'ecase.explorer.documentList.archive.awfCompleted'
};

const ARCHIVE_APPROVAL_NOT_STOPPED: OutcomeMessage = {
  status: 'failure',
  key: 'ecase.explorer.documentList.archive.approvalNotStopped'
};

const ARCHIVE_FAILED: OutcomeMessage = {
  status: 'failure',
  key: 'ecase.explorer.documentList.archive.error'
};

export const ARCHIVE_OUTCOME_UNKNOWN: OutcomeMessage = {
  status: 'warning',
  key: 'ecase.explorer.documentList.archive.outcomeUnknown'
};

export function manualStartOutcome(approvalWorkflow: string | undefined): OutcomeMessage {
  switch (approvalWorkflow) {
    case 'RESTARTED':
      return {
        status: 'success',
        key: 'ecase.explorer.documentList.approval.started'
      };
    case 'CONFLICT':
      return CONFLICT;
    case 'ALREADY_RUNNING':
    case 'ALREADY_COMPLETE':
    case 'MISCONFIGURED':
    case 'NOT_APPLICABLE':
    case 'DOCUMENT_ARCHIVED':
    case 'AWF_COMPLETED':
    case 'UNAVAILABLE':
      return UNAVAILABLE;
    case 'FAILED':
      return {...NOT_STARTED, status: 'failure'};
    default:
      return NOT_STARTED;
  }
}

export function replaceOutcome(approvalWorkflow: string, replaced: boolean): OutcomeMessage {
  if (approvalWorkflow === 'AWF_COMPLETED') {
    return {
      status: 'warning',
      key: 'ecase.explorer.documentList.replace.awfCompleted'
    };
  }
  if (!replaced && approvalWorkflow !== 'CONFLICT') {
    return REPLACE_FAILED;
  }
  switch (approvalWorkflow) {
    case 'RESTARTED':
    case 'NOT_APPLICABLE':
      return {
        status: 'success',
        key: 'ecase.explorer.documentList.replace.success'
      };
    case 'MISCONFIGURED':
    case 'FAILED':
      return {
        status: 'warning',
        key: 'ecase.explorer.documentList.replace.approvalNotRestarted'
      };
    case 'CONFLICT':
      return CONFLICT;
    default:
      return OUTCOME_UNKNOWN;
  }
}

export interface ArchiveOutcome extends OutcomeMessage {
  retryable: boolean;
}

export function archiveOutcome(approvalWorkflow: string | undefined): ArchiveOutcome {
  switch (approvalWorkflow) {
    case 'STOPPED':
    case 'NOT_APPLICABLE':
      return {...ARCHIVED, retryable: false};
    case 'ALREADY_APPROVED':
      return {...ARCHIVE_APPROVED, retryable: false};
    case 'AWF_COMPLETED':
      return {...ARCHIVE_AWF_COMPLETED, retryable: false};
    case 'CONFLICT':
      return {...CONFLICT, retryable: true};
    case 'FAILED':
      return {...ARCHIVE_APPROVAL_NOT_STOPPED, retryable: true};
    default:
      return {...ARCHIVE_FAILED, retryable: true};
  }
}